import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';

const TransactionsTab = ({ transactions = [] }) => { // Accept transactions as prop
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState(10);
    const [loading, setLoading] = useState(false);
    const [transactionData, setTransactionData] = useState(transactions);

    // Optional: Fetch data from API
    useEffect(() => {
        if (transactions.length === 0) {
            fetchTransactions();
        } else {
            setTransactionData(transactions);
        }
    }, [transactions]);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            // Replace with your actual API endpoint
            const response = await fetch('/api/transactions');
            const data = await response.json();
            setTransactionData(data);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setTransactionData([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter transactions based on search term
    const filteredTransactions = transactionData.filter(transaction => 
        Object.values(transaction).some(value => 
            value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const header = (
        <div className="flex align-items-center justify-content-between">
            <div>
                <h5 className="m-0">Transactions ({filteredTransactions.length})</h5>
            </div>
            <div className="flex align-items-center gap-2">
                <Button 
                    type="button" 
                    label="Refresh" 
                    icon="pi pi-refresh"
                    onClick={fetchTransactions}
                    loading={loading}
                />
                <Button type="button" label="CSV" rounded onClick={() => exportCSV()} data-pr-tooltip="CSV" />
                <Button type="button" label="XLS" severity="success" rounded onClick={() => exportXLS()} data-pr-tooltip="XLS" />
                <Button type="button" label="PDF" severity="warning" rounded onClick={() => exportPDF()} data-pr-tooltip="PDF" />
            </div>
        </div>
    );

    // Pagination options
    const rowsPerPageOptions = [10, 20, 50, 100];

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'paid': 'bg-success',
            'partial': 'bg-warning',
            'unpaid': 'bg-danger'
        };

        return (
            <span className={`badge ${statusColors[rowData.status] || 'bg-secondary'}`}>
                {rowData.status ? rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1) : 'Unknown'}
            </span>
        );
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(value || 0);
    };

    const amountBodyTemplate = (rowData) => {
        return formatCurrency(rowData.total_amount);
    };

    const dateBodyTemplate = (rowData) => {
        if (!rowData.added_on) return '-';
        return new Date(rowData.added_on).toLocaleDateString('en-KE');
    };

    // Export functions (implement based on your needs)
    const exportCSV = () => {
        // Implement CSV export logic
        console.log('Exporting to CSV...');
    };

    const exportXLS = () => {
        // Implement XLS export logic
        console.log('Exporting to XLS...');
    };

    const exportPDF = () => {
        // Implement PDF export logic
        console.log('Exporting to PDF...');
    };

    return (
        <div className="card">
            <div className="card-body">
                <div className="col-md-3 col-xs-12 mb-3">
                    <input
                        className="form-control"
                        placeholder="Search transactions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="dt-responsive table-responsive">
                    <DataTable
                        value={filteredTransactions}
                        header={header}
                        loading={loading}
                        emptyMessage="No transactions found."
                        sortMode="multiple"
                        paginator
                        rows={rows}
                        rowsPerPageOptions={rowsPerPageOptions}
                        stripedRows
                        tableStyle={{ minWidth: '50rem' }}
                        onRowsChange={(e) => setRows(e.value)}
                    >
                        <Column 
                            field="transaction_ref" 
                            header="Transaction Ref"
                            sortable
                        />
                        <Column 
                            field="customer" 
                            header="Customer"
                            sortable
                        />
                        <Column 
                            field="unit" 
                            header="Unit No"
                            sortable
                        />
                        <Column 
                            field="payment_method" 
                            header="Payment Method"
                            sortable
                        />
                        <Column 
                            field="total_amount" 
                            header="Total Amount"
                            body={amountBodyTemplate}
                            sortable
                        />
                        <Column 
                            field="added_on" 
                            header="Added On"
                            body={dateBodyTemplate}
                            sortable
                        />
                        <Column 
                            field="status" 
                            header="Status"
                            body={statusBodyTemplate}
                            sortable
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default TransactionsTab;